import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Container } from '../components/Container';
import { CheckCircle2, ArrowRight, Star, ShieldCheck } from 'lucide-react';
import { motion } from 'framer-motion';
// Sport Data Map
const SPORT_DATA: Record<string, any> = {
  baseball: {
    name: 'Baseball',
    heroImage: 'https://images.unsplash.com/photo-1508344928928-7165b67de128?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80',
    desc: 'Premium custom baseball uniforms built for performance on the diamond. From full-button jerseys to durable pants.',
    styles: ['Full Button Jerseys', 'Two-Button Jerseys', 'V-Neck Jerseys', 'Performance Pants', 'Batting Jackets', 'Hats & Visors']
  },
  basketball: {
    name: 'Basketball',
    heroImage: 'https://images.unsplash.com/photo-1546519638-68e109498ffc?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80',
    desc: 'Lightweight, breathable custom basketball uniforms. Reversible options and shooter shirts available.',
    styles: ['Reversible Jerseys', 'Game Shorts', 'Shooter Shirts', 'Warm-up Suits', 'Practice Reversibles', 'Travel Gear']
  },
  football: {
    name: 'Football',
    heroImage: 'https://images.unsplash.com/photo-1566577739112-5180d4bf9390?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80',
    desc: 'Tough, durable custom football jerseys and pants designed to withstand the hardest hits.',
    styles: ['Game Jerseys', 'Integrated Pants', 'Practice Jerseys', '7v7 Uniforms', 'Compression Gear', 'Coaches Polos']
  },
  softball: {
    name: 'Softball',
    heroImage: 'https://images.unsplash.com/photo-1550416956-2cb63406e123?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80',
    desc: 'Custom softball uniforms tailored for the perfect fit. Sublimated designs that never fade or crack.',
    styles: ['Sleeveless Jerseys', 'Two-Button Jerseys', 'Softball Pants', 'Shorts', 'Pullover Jackets', 'Visors']
  },
  soccer: {
    name: 'Soccer',
    heroImage: 'https://images.unsplash.com/photo-1518605368461-1e1e1141528b?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80',
    desc: 'Complete custom soccer kits. Lightweight moisture-wicking fabrics for peak performance on the pitch.',
    styles: ['Game Jerseys', 'Game Shorts', 'Goalie Kits', 'Warm-up Jackets', 'Training Tops', 'Socks']
  },
  cheer: {
    name: 'Cheer',
    heroImage: 'https://images.unsplash.com/photo-1565019001306-3844622b753a?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80',
    desc: 'Vibrant, flexible custom cheer uniforms and warm-ups designed for movement and team spirit.',
    styles: ['Shell Tops', 'Skirts & Skorts', 'Warm-up Suits', 'Practice Wear', 'Bags & Backpacks', 'Bows']
  },
  volleyball: {
    name: 'Volleyball',
    heroImage: 'https://images.unsplash.com/photo-1592656094267-764a45160876?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80',
    desc: 'Custom volleyball jerseys and spandex built for agility and comfort on the court.',
    styles: ['Long Sleeve Jerseys', 'Short Sleeve Jerseys', 'Sleeveless Jerseys', 'Spandex Shorts', 'Warm-ups', 'Knee Pads']
  },
  'track-field': {
    name: 'Track & Field',
    heroImage: 'https://images.unsplash.com/photo-1552674605-15c9ef04305c?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80',
    desc: 'Aerodynamic custom track uniforms. Singlets and compression gear for speed and endurance.',
    styles: ['Singlets', 'Compression Shorts', 'Running Shorts', 'Warm-up Suits', 'T-Shirts', 'Bags']
  },
  groupwear: {
    name: 'Groupwear',
    heroImage: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80',
    desc: 'Custom apparel for corporate teams, staff, clubs, and organizations. Professional and unified.',
    styles: ['Polos', 'Quarter Zips', 'Hoodies', 'T-Shirts', 'Jackets', 'Hats']
  }
};
export function SportDetail() {
  const {
    sport
  } = useParams<{
    sport: string;
  }>();
  const data = sport && SPORT_DATA[sport] ? SPORT_DATA[sport] : SPORT_DATA['baseball']; // Fallback
  // Scroll to top on mount/sport change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [sport]);
  return <div className="w-full bg-white" data-id="element-546">
      {/* Hero Section */}
      <section className="relative bg-[#111827] text-white overflow-hidden py-20 lg:py-32" data-id="element-547">
        <div className="absolute inset-0 z-0" data-id="element-548">
          <img src={data.heroImage} alt={`Custom ${data.name} Uniforms`} className="w-full h-full object-cover opacity-40" data-id="element-549" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#111827] via-[#111827]/80 to-transparent" data-id="element-550"></div>
        </div>

        <Container className="relative z-10" data-id="element-551">
          <div className="max-w-2xl" data-id="element-552">
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="font-oswald text-5xl md:text-6xl font-bold uppercase mb-4" data-id="element-553">
              Custom <span className="text-pts-red" data-id="element-554">{data.name}</span> Uniforms
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="text-xl text-gray-300 mb-8" data-id="element-555">
              {data.desc}
            </motion.p>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} data-id="element-556">
              <Link to="/get-started" className="inline-block bg-pts-red hover:bg-pts-red-dark text-white px-8 py-4 rounded font-bold uppercase tracking-wide text-lg transition-colors" data-id="element-557">
                Start My {data.name} Order
              </Link>
            </motion.div>
          </div>
        </Container>
      </section>

      {/* We Beat All Quotes Band */}
      <div className="bg-pts-red text-white py-4" data-id="element-558">
        <Container data-id="element-559">
          <div className="flex flex-col sm:flex-row items-center justify-center text-center sm:text-left gap-4" data-id="element-560">
            <ShieldCheck className="h-8 w-8 flex-shrink-0" data-id="element-561" />
            <div data-id="element-562">
              <span className="font-oswald text-2xl font-bold uppercase tracking-wide mr-2" data-id="element-563">
                We Beat All Quotes.
              </span>
              <span className="text-white/90 font-medium" data-id="element-564">
                Send us your current quote and we'll beat the pricing
                guaranteed.
              </span>
            </div>
          </div>
        </Container>
      </div>

      {/* Uniform Styles */}
      <section className="py-20 bg-gray-50" data-id="element-565">
        <Container data-id="element-566">
          <div className="text-center mb-12" data-id="element-567">
            <h2 className="font-oswald text-4xl font-bold uppercase mb-4 text-gray-900" data-id="element-568">
              {data.name} Styles & Options
            </h2>
            <div className="w-24 h-1.5 bg-pts-red mx-auto mb-6" data-id="element-569"></div>
            <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-570">
              Fully sublimated designs mean unlimited colors, logos, and numbers
              included in one flat price.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6" data-id="element-571">
            {data.styles.map((style: string, index: number) => <div key={index} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 text-center hover:border-pts-red transition-colors group" data-id="element-572">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-red-50 group-hover:text-pts-red transition-colors" data-id="element-573">
                  <CheckCircle2 className="h-6 w-6 text-gray-400 group-hover:text-pts-red" data-id="element-574" />
                </div>
                <h3 className="font-bold text-gray-900" data-id="element-575">{style}</h3>
              </div>)}
          </div>
        </Container>
      </section>

      {/* Package Options */}
      <section className="py-20 bg-white border-t border-gray-200" data-id="element-576">
        <Container data-id="element-577">
          <div className="flex flex-col lg:flex-row items-center gap-12" data-id="element-578">
            <div className="lg:w-1/2" data-id="element-579">
              <h2 className="font-oswald text-4xl font-bold uppercase mb-6 text-gray-900" data-id="element-580">
                Build a {data.name} Team Package
              </h2>
              <div className="w-24 h-1.5 bg-pts-red mb-6" data-id="element-581"></div>
              <p className="text-lg text-gray-600 mb-6" data-id="element-582">
                Bundle your game uniforms with warm-ups, bags, and coaches gear
                to save money and keep your entire program looking unified.
              </p>
              <ul className="space-y-3 mb-8" data-id="element-583">
                <li className="flex items-center text-gray-700" data-id="element-584">
                  <CheckCircle2 className="h-5 w-5 text-pts-red mr-3" data-id="element-585" />{' '}
                  Consistent colors across all garments
                </li>
                <li className="flex items-center text-gray-700" data-id="element-586">
                  <CheckCircle2 className="h-5 w-5 text-pts-red mr-3" data-id="element-587" /> Volume
                  discounts applied
                </li>
                <li className="flex items-center text-gray-700" data-id="element-588">
                  <CheckCircle2 className="h-5 w-5 text-pts-red mr-3" data-id="element-589" /> Easy
                  ordering for the whole organization
                </li>
              </ul>
              <Link to="/get-started" className="text-pts-red font-bold uppercase tracking-wider flex items-center hover:text-pts-red-dark" data-id="element-590">
                Inquire About Packages <ArrowRight className="ml-2 h-4 w-4" data-id="element-591" />
              </Link>
            </div>
            <div className="lg:w-1/2 w-full" data-id="element-592">
              <div className="bg-gray-100 rounded-xl p-8 border border-gray-200" data-id="element-593">
                <div className="grid grid-cols-2 gap-4" data-id="element-594">
                  <div className="bg-white p-4 rounded shadow-sm text-center font-bold text-gray-800" data-id="element-595">
                    Game Jersey
                  </div>
                  <div className="bg-white p-4 rounded shadow-sm text-center font-bold text-gray-800" data-id="element-596">
                    Game Pants/Shorts
                  </div>
                  <div className="bg-white p-4 rounded shadow-sm text-center font-bold text-gray-800" data-id="element-597">
                    Warm-up Top
                  </div>
                  <div className="bg-white p-4 rounded shadow-sm text-center font-bold text-gray-800" data-id="element-598">
                    Team Bag
                  </div>
                </div>
                <div className="mt-6 text-center" data-id="element-599">
                  <span className="inline-block bg-gray-900 text-white px-4 py-1 rounded-full text-sm font-bold uppercase tracking-wider" data-id="element-600">
                    Custom Bundle
                  </span>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Mini Reviews */}
      <section className="py-16 bg-[#111827] text-white" data-id="element-601">
        <Container data-id="element-602">
          <div className="text-center mb-10" data-id="element-603">
            <h3 className="font-oswald text-2xl font-bold uppercase tracking-wide" data-id="element-604">
              What Teams Are Saying
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-605">
            {[1, 2, 3].map((_, i) => <div key={i} className="bg-gray-800 p-6 rounded border border-gray-700 text-center" data-id="element-606">
                <div className="flex justify-center text-pts-red mb-3" data-id="element-607">
                  {[...Array(5)].map((_, j) => <Star key={j} className="h-4 w-4 fill-current" data-id="element-608" />)}
                </div>
                <p className="text-gray-300 text-sm italic mb-4" data-id="element-609">
                  "The quality of these uniforms exceeded our expectations. The
                  colors pop and the fit is perfect for our athletes."
                </p>
                <div className="font-bold text-sm uppercase text-gray-400" data-id="element-610">
                  - Coach Review
                </div>
              </div>)}
          </div>
        </Container>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gray-50 text-center" data-id="element-611">
        <Container data-id="element-612">
          <h2 className="font-oswald text-4xl font-bold uppercase mb-6 text-gray-900" data-id="element-613">
            Ready for Your {data.name} Mockups?
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8" data-id="element-614">
            Place an art deposit today and our design team will get to work on
            your custom concepts.
          </p>
          <Link to="/get-started" className="inline-block bg-pts-red hover:bg-pts-red-dark text-white px-8 py-4 rounded font-bold uppercase tracking-wide text-lg transition-colors shadow-lg" data-id="element-615">
            Start My Order
          </Link>
        </Container>
      </section>
    </div>;
}